import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import './SelectAnalyzersTable.scss';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../../../shared/types';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {
  setFields,
  IRequestField,
  ISelectedMetadataFieldName,
} from '../../../../../../redux/slice/createCustomIndexSlice';
import {useMemo} from 'react';

type SelectedMetadataField = ISelectedMetadataFieldName & {
  analyzerID?: number;
  analyzerId?: number;
  analyzerName?: string;
  fieldType?: string;
  analyzerType?: string;
};

type SelectAnalyzersTableProps = {
  analyzerResponse?: unknown;
  selectedMetadataFieldNames?: SelectedMetadataField[];
  analyzerColumnName?: string;
  fullWidth?: boolean;
  editableOnly?: boolean;
};

type CustomIndexAnalyzerResponseItem = {
  analyzerID?: number;
  analyzerId?: number;
  analyzerName?: string;
  analyzerKey?: string | null;
  description?: string | null;
  AnalyzerID?: number;
  AnalyzerId?: number;
  AnalyzerName?: string;
  AnalyzerKey?: string | null;
  Description?: string | null;
  AnalyzerType?: string;
  analyzerType?: string;
  customAnalyzer?: boolean;
};

type AnalyzerTableRowData = {
  id: string;
  fieldName: string;
  analyzerId?: number;
  analyzerKey: string;
  analyzerName?: string;
  fieldType?: string;
  isDefault: boolean;
  isAvailableForCustomIndex: boolean;
};

const SelectAnalyzersTable = ({
  analyzerResponse,
  selectedMetadataFieldNames = [],
  analyzerColumnName,
  fullWidth = false,
  editableOnly = false,
}: SelectAnalyzersTableProps) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {isReadOnlyView, createCustomIndex} = useSelector(
    (state: RootState) => state.indexManagement
  );
  const storedFields: IRequestField[] = createCustomIndex.fields ?? [];

  const analyzers: CustomIndexAnalyzerResponseItem[] = Array.isArray(
    analyzerResponse
  )
    ? (analyzerResponse as CustomIndexAnalyzerResponseItem[])
    : [];

  const analyzerDropDownItems: ISelectItem[] = analyzers.map((analyzer) => {
    const analyzerId =
      analyzer.analyzerID ??
      analyzer.analyzerId ??
      analyzer.AnalyzerID ??
      analyzer.AnalyzerId;
    const analyzerKey = analyzer.analyzerKey ?? analyzer.AnalyzerKey;
    const analyzerName =
      analyzer.analyzerName ?? analyzer.AnalyzerName ?? analyzerKey;
    return {
      id: analyzerId,
      name: analyzerName ?? '',
      analyzerType: analyzer.analyzerType ?? analyzer.AnalyzerType,
      customAnalyzer: analyzer.customAnalyzer,
    };
  });
  const analyzerTablerows: AnalyzerTableRowData[] =
    storedFields.length > 0
      ? storedFields.map((field) => {
          const matchedSelectedFieldName = selectedMetadataFieldNames.find(
            (selectedField) =>
              Number(selectedField.fieldId) === Number(field.fieldId)
          );
          const matchedAnalyzer = analyzers.find((analyzer) => {
            const analyzerId =
              analyzer.analyzerID ??
              analyzer.analyzerId ??
              analyzer.AnalyzerID ??
              analyzer.AnalyzerId;
            return Number(analyzerId) === Number(field.analyzerId);
          });
          return {
            id: String(field.fieldId),
            fieldName: String(
              matchedSelectedFieldName?.fieldName ?? `Field ${field.fieldId}`
            ),
            analyzerId: Number(field.analyzerId),
            analyzerKey: String(
              matchedAnalyzer?.analyzerKey ?? matchedAnalyzer?.AnalyzerKey ?? ''
            ),
            analyzerName:
              matchedAnalyzer?.analyzerName ??
              matchedAnalyzer?.AnalyzerName ??
              matchedSelectedFieldName?.analyzerName,
            fieldType: matchedSelectedFieldName?.fieldType,
            isDefault: matchedSelectedFieldName?.isDefault ?? false,
            isAvailableForCustomIndex: matchedAnalyzer?.customAnalyzer ?? false,
          };
        })
      : selectedMetadataFieldNames.map((field) => {
          const matchedAnalyzer = analyzers.find((analyzer) => {
            const analyzerId =
              analyzer.analyzerID ??
              analyzer.analyzerId ??
              analyzer.AnalyzerID ??
              analyzer.AnalyzerId;
            return (
              Number(analyzerId) ===
              Number(field.analyzerID ?? field.analyzerId)
            );
          });
          return {
            id: String(field.fieldId),
            fieldName: String(field.fieldName ?? `Field ${field.fieldId}`),
            analyzerId: Number(field.analyzerID ?? field.analyzerId),
            analyzerKey: String(
              matchedAnalyzer?.analyzerKey ??
                matchedAnalyzer?.AnalyzerKey ??
                field.analyzerKey ??
                ''
            ),
            analyzerName:
              matchedAnalyzer?.analyzerName ??
              matchedAnalyzer?.AnalyzerName ??
              field.analyzerName,
            fieldType: field.fieldType,
            isDefault: field.isDefault ?? false,
            isAvailableForCustomIndex: matchedAnalyzer?.customAnalyzer ?? false,
          };
        });

  const sortedanalyzerTablerows = [...(analyzerTablerows ?? [])].sort((a, b) =>
    a?.fieldName.localeCompare(b?.fieldName)
  );

  const visibleAnalyzerTableRows = editableOnly
    ? sortedanalyzerTablerows.filter((row) => row.isAvailableForCustomIndex)
    : sortedanalyzerTablerows;

  const handleChange = (
    value: ISelectItem | null,
    row: AnalyzerTableRowData
  ) => {
    const updatedFields = storedFields.map((field) =>
      Number(field.fieldId) === Number(row.id)
        ? {
            ...field,
            analyzerId: value?.id ? Number(value.id) : field.analyzerId,
          }
        : field
    );
    dispatch(setFields(updatedFields));
  };

  const customDropDownItems = useMemo(() => {
    return analyzerDropDownItems.filter((analyzer) => analyzer.customAnalyzer);
  }, [analyzerDropDownItems]);

  return (
    <Box
      className={classNames(
        'select-analyzers-table',
        isReadOnlyView && 'select-analyzers-readonly-table',
        fullWidth && 'select-analyzers-full-width'
      )}
    >
      <Table
        aria-label="analyzer-selection-table"
        sx={{
          ...(fullWidth && {
            borderRadius: '0.5rem',
            overflow: 'hidden',
          }),
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              tabIndex={0}
              className="select-analyzer-field-column typo-text label2_size"
            >
              {analyzerColumnName ??
                t('indexManagement.customIndex.selectAnalyzer.selectedFields')}
            </TableCell>
            <TableCell
              tabIndex={0}
              className="select-analyzer-column typo-text label2_size"
            >
              {t('indexManagement.customIndex.selectAnalyzer.analyzerName')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleAnalyzerTableRows?.map((row) => (
            <TableRow key={row.id}>
              <TableCell
                tabIndex={0}
                className={classNames(
                  'select-analyzer-field-column',
                  isReadOnlyView && 'select-analyzer-readonly',
                  'table-row typo-text body3_size'
                )}
              >
                {row.fieldName}
              </TableCell>
              <TableCell
                tabIndex={isReadOnlyView ? 0 : -1}
                className={classNames(
                  'select-analyzer-column',
                  ' typo-text body3_size',
                  isReadOnlyView
                    ? 'select-analyzer-readonly'
                    : 'table-row-analyzer'
                )}
              >
                {isReadOnlyView ? (
                  row.analyzerName ?? row.analyzerKey
                ) : (
                  <ArrSingleSelectDropdown
                    key={row.id}
                    dropdownItems={customDropDownItems}
                    dropdownLabel="Select Analyzer"
                    selectedValues={{
                      id: row.analyzerId,
                      name: row.analyzerName ?? row.analyzerKey,
                    }}
                    onSelectionChange={(value) => handleChange(value, row)}
                    sizeVariant="small"
                    disabled={!row.isAvailableForCustomIndex}
                    dataTestId={`asd-selection-analyzer-table-dropdown-${row.id}`}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default SelectAnalyzersTable;
